/**
 * Recently Viewed Products Module
 * Handles tracking and displaying recently viewed products
 */

import { SWIPER_DRAG_DEFAULTS } from './swiper-feel.js';

class RecentlyViewed {
    constructor() {
      this.storageKey = 'recently_viewed';
      this.maxProducts = this.getMaxProducts();
      this.init();
    }
  
    /**
     * Get maximum number of products from data attribute
     */
    getMaxProducts() {
      const element = document.querySelector('recently-viewed');
      return parseInt(element?.getAttribute("data-products-count") || "8", 10);
    }
  
    /**
     * Escape quotes in strings to prevent JavaScript injection
     */
    escapeQuotes(str) {
      if (typeof str !== 'string') return str;
      return str.replace(/'/g, "\\'").replace(/"/g, '\\"');
    }
  
    /**
     * Get existing recently viewed data from sessionStorage
     */
    getStoredData() {
      try {
        const data = sessionStorage.getItem(this.storageKey);
        return data ? JSON.parse(data) : [];
      } catch (error) {
        return [];
      }
    }
  
    /**
     * Save data to sessionStorage
     */
    saveData(data) {
      try {
        sessionStorage.setItem(this.storageKey, JSON.stringify(data));
      } catch (error) {
        // Silently handle storage errors
      }
    }
  
    /**
     * Add current product to recently viewed list
     */
    addCurrentProduct() {
      if (!pdpData?.productTitle?.trim()) {
        return;
      }
  
      const currentProduct = {
        ...pdpData,
        productTitle: this.escapeQuotes(pdpData.productTitle.trim())
      };
      const existingData = this.getStoredData();
      const currentTitle = currentProduct.productTitle;
  
      // Check if product is already in the list
      const isDuplicate = existingData.some(item => item.productTitle === currentTitle);
      
      if (isDuplicate) {
        return;
      }
  
      // Add new product to the beginning
      const updatedData = [currentProduct, ...existingData];
  
      // Limit to max products
      if (updatedData.length > this.maxProducts) {
        updatedData.splice(this.maxProducts);
      }
  
    this.saveData(updatedData);
  }

  /**
   * Generate HTML for recently viewed products
   */
  generateProductHTML(product) {
    const escapedTitle = this.escapeQuotes(product.productTitle || '');
    return `
      <div>
        <product-card class="w-fit block border border-theme-border border-solid">
          <a class="contents" href="${product.productUrl}" title="${escapedTitle}" aria-label="${escapedTitle}">
            <div class="product-media-container media-fit product-media-container--image">
              <div class="product-media">
                ${product.productImg}
              </div>
            </div>
          </a>
        </product-card>
      </div>
    `;
  }

  /**
   * Render recently viewed products to the page
   */
  renderProducts() {
    const products = this.getStoredData();
    
    if (!products.length) {
      return;
    }

    const limitedProducts = products.slice(0, this.maxProducts);
    
    // Set data-has-recently-viewed to true so the section is shown
    const recentlyViewedElement = document.querySelector('recently-viewed');
    if (recentlyViewedElement) {
      recentlyViewedElement.setAttribute('data-has-recently-viewed', 'true');
      const section = recentlyViewedElement.closest('section');
      if (section) {
        section.setAttribute('data-has-recently-viewed', 'true');
      }
    }
    
    // One .slider-content per product list (avoid matching both [data-slider-block] and nested .slider-content twice)
    document.querySelectorAll('recently-viewed .slider-content').forEach((contentEl) => {
      this.renderContainer(contentEl, limitedProducts);
    });

    this.initializeSliders();
  }

  /**
   * Render products in container
   */
  renderContainer(element, products) {
    const html = products
      .map(product => this.generateProductHTML(product))
      .join('');
    
    element.innerHTML = html;
  }

  /** Matches Tailwind md: (768px) — slider vs grid in _recently-viewed-product-list */
  static SLIDER_VIEWPORT_MQ = '(max-width: 767px)';

  wantsSwiperForBlock(element) {
    const mode = element.dataset.sliderVisibility || 'both';
    const isMobile = window.matchMedia(RecentlyViewed.SLIDER_VIEWPORT_MQ).matches;
    if (mode === 'both') return true;
    if (mode === 'mobile') return isMobile;
    if (mode === 'desktop') return !isMobile;
    return true;
  }

  normalizeSwiperOptionsForVisibility(element, customOptions) {
    const opts = { ...customOptions };
    const mode = element.dataset.sliderVisibility || 'both';
    const bp = opts.breakpoints?.['768'] ?? opts.breakpoints?.[768];
    if (mode === 'mobile') {
      delete opts.breakpoints;
      opts.loop = false;
    } else if (mode === 'desktop' && bp && typeof bp === 'object') {
      Object.assign(opts, bp);
      delete opts.breakpoints;
    }
    return opts;
  }

  destroySingleSlider(element) {
    const contentEl = element.querySelector('.slider-content');
    if (!contentEl) return;
    const swiperEl = contentEl.querySelector(':scope > .slider-block-swiper');
    if (!swiperEl) return;

    const inst = swiperEl.swiper;
    if (inst) {
      inst.destroy(false, false);
    }

    const wrapper = swiperEl.querySelector('.swiper-wrapper');
    if (wrapper) {
      while (wrapper.firstChild) {
        const node = wrapper.firstChild;
        node.classList?.remove('swiper-slide', 'swiper-slide-visible', 'swiper-slide-active');
        contentEl.insertBefore(node, swiperEl);
      }
    }
    swiperEl.remove();
  }

  syncSliderBlock(element) {
    if (!this.wantsSwiperForBlock(element)) {
      this.destroySingleSlider(element);
      return;
    }
    if (element.querySelector('.slider-block-swiper')) {
      return;
    }
    this.initializeSingleSlider(element);
  }

  /**
   * Initialize sliders after products are rendered (respects data-slider-visibility: mobile | desktop | both)
   */
  initializeSliders() {
    const sliderBlocks = document.querySelectorAll('recently-viewed [data-slider-block]');
    if (sliderBlocks.length === 0) return;

    if (!this._sliderViewportListenerBound) {
      this._sliderViewportListenerBound = true;
      window.matchMedia(RecentlyViewed.SLIDER_VIEWPORT_MQ).addEventListener('change', () => {
        document.querySelectorAll('recently-viewed [data-slider-block]').forEach((block) => {
          this.syncSliderBlock(block);
        });
      });
    }

    const run = () => {
      if (typeof window.Swiper === 'undefined') {
        setTimeout(run, 100);
        return;
      }
      sliderBlocks.forEach((block) => this.syncSliderBlock(block));
    };

    run();
  }

  /**
   * Build Swiper inside block (call only when wantsSwiperForBlock is true)
   */
  initializeSingleSlider(element) {
    const contentEl = element.querySelector('.slider-content');
    if (!contentEl) return;
    if (contentEl.querySelector(':scope > .slider-block-swiper')) return;

    const slides = [...contentEl.children].filter((el) =>
      !['STYLE', 'SCRIPT', 'LINK', 'META', 'NOSCRIPT', 'TEMPLATE'].includes(el.tagName)
    );

    if (slides.length < 1) return;

    const swiperEl = document.createElement('div');
    swiperEl.classList.add('slider-block-swiper', 'w-full', 'max-w-full', 'overflow-x-hidden');

    const wrapperEl = document.createElement('div');
    wrapperEl.classList.add('swiper-wrapper');

    slides.forEach((slide) => {
      slide.classList.add('swiper-slide');
      wrapperEl.appendChild(slide);
    });

    swiperEl.appendChild(wrapperEl);
    contentEl.innerHTML = '';
    contentEl.appendChild(swiperEl);

    const rawOptions = element.dataset.swiperOptions ? JSON.parse(element.dataset.swiperOptions) : {};
    const customOptions = this.normalizeSwiperOptionsForVisibility(element, rawOptions);

    const defaultOptions = {
      ...SWIPER_DRAG_DEFAULTS,
      slidesPerView: 1,
      spaceBetween: 16,
      allowTouchMove: true,
      simulateTouch: true,
      grabCursor: true,
    };

    if (slides.length > 1 && customOptions.loop !== false) {
      defaultOptions.loop = true;
    }

    const blockId = element.dataset.blockId;
    const controlsElement = element.querySelector('[data-slider-control]');

    if (controlsElement && blockId) {
      const nextBtn = controlsElement.querySelector('.slider-navigation-next');
      const prevBtn = controlsElement.querySelector('.slider-navigation-prev');

      if (nextBtn && prevBtn) {
        customOptions.navigation = {
          nextEl: nextBtn,
          prevEl: prevBtn,
        };
      }
    }

    new window.Swiper(swiperEl, {
      ...defaultOptions,
      ...customOptions,
    });
  }

  /**
   * Initialize the recently viewed functionality
   */
  init() {
    // Add current product to recently viewed
    this.addCurrentProduct();

    // Render on DOM ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.renderProducts());
    } else {
      this.renderProducts();
    }
  }
}
  
  // Initialize the module
  new RecentlyViewed();